import { CoreFetch, ReportApi } from '@enter/core';
import { ContactSupport, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { IInjectedRootStore, stream2string } from 'stores';
import * as S from './reports.styles';

type IReportsProps = IInjectedRootStore;

export const Reports = inject('rootStore')(
    observer((props: IReportsProps) => {
        const { rootStore } = props;

        const [loading, setLoading] = useState(true);
        const [unavailable, setUnavailable] = useState(false);
        const [error, setError] = useState(false);
        const [demoMode, setDemoMode] = useState(false);

        document.title = 'Provider Dashboard - Reports';

        useEffect(() => {
            (async (): Promise<void> => {
                if(!rootStore.isDemo()) {
                    try {
                        setLoading(true);
                        const { url } = await CoreFetch<ReportApi>(ReportApi).getProviderQuicksightUrl();
                        const QuickSightEmbedding = await import('amazon-quicksight-embedding-sdk');
                        
                        const embeddingContext = await QuickSightEmbedding.createEmbeddingContext();
                        
                        const options = {
                            url,
                            container: document.getElementById('dashboardContainer'),
                            height: '100%',
                            width: '100%',
                            onChange: (changeEvent) => {
                                switch (changeEvent.eventName) {
                                    case 'CONTENT_LOADED':
                                        setError(false);
                                        setLoading(false);
                                        break;
                                    case 'ERROR_OCCURRED':
                                        setError(true);
                                        setLoading(false);
                                        break;
                                }
                            },
                        };

                        const contentOptions = {
                            onMessage: async (messageEvent, experienceMetadata) => {
                                console.log("Quicksight Message received: ", messageEvent);
                                switch (messageEvent.eventName) {
                                    case 'CONTENT_LOADED':
                                        setError(false);
                                        setLoading(false);
                                        break;
                                    case 'ERROR_OCCURRED':
                                        setError(true);
                                        setLoading(false);
                                        break;
                                }
                            }
                        };
                        
                        const dashboard = await embeddingContext.embedDashboard(options, contentOptions);
                    } catch (err) {
                        // tslint:disable-next-line: no-console
                        console.error(err);

                        if (err && err.body) {
                            const str = await stream2string(err.body);
                            const msg = JSON.parse(str);
                            if (msg.code === 'err.quicksight_configuration_does_not_exist') {
                                setUnavailable(true);
                            } else {
                                setError(true);
                            }
                        } else {
                            setError(true);
                        }
                        setLoading(false);
                    }
                } else {
                    setLoading(false);
                    setDemoMode(true);
                }
            })();
        }, []);

        const renderLoader = (): React.ReactElement => <S.Loader />;
        const renderUnavailable = (): React.ReactElement => (
            <S.Message>
                Sorry! Your account is not configured for reports.
                <ContactSupport action="request access to reports" subject="Requesting access to reports" />
            </S.Message>
        );
        const renderError = (): React.ReactElement => (
            <S.Message>
                Sorry! An error occurred while loading your reports.
                <ContactSupport action="request help or refresh to try again" subject="Need help with reports" />
            </S.Message>
        );

        const renderDemo = (): React.ReactElement => {
            return <iframe style={{border: '1px solid rgba(0, 0, 0, 0.1)'}} width="100%" height="100%"
                           src="https://www.figma.com/embed?embed_host=share&url=https%3A%2F%2Fwww.figma.com%2Fproto%2FTOSxE6fObTOPzuwuh6m1r2%2FEnter-Design-Work%3Fnode-id%3D2802-16320%26t%3DrIUS6yVzl5oIevZb-1%26scaling%3Dscale-down-width%26content-scaling%3Dfixed%26page-id%3D2446%253A9041%26starting-point-node-id%3D2802%253A16315%26hide-ui%3D1"
                           allowFullScreen>
            </iframe>;
        };
        const renderConditional = (): React.ReactElement | undefined => {
            if (loading) {
                return renderLoader();
            } else if (unavailable) {
                return renderUnavailable();
            } else if (error) {
                return renderError();
            } else if(demoMode) {
                return renderDemo();
            }
        };

        return (
            <S.Wrapper>
                <PageLayoutRow expand={!unavailable}>
                    {renderConditional()}
                    {!unavailable && <S.Embedded id="dashboardContainer" />}
                </PageLayoutRow>
            </S.Wrapper>
        );
    }),
);
